import { TerminologyProfile } from '../config/terminologyMap';

function resolveTokens(template: string, t: TerminologyProfile): string {
  return template
    .replace(/\{\{req_section_label\}\}/g, t.req_section_label || 'Regulations/Obligations related to Control')
    .replace(/\{\{req_field_label\}\}/g, t.req_field_label || 'Obligation')
    .replace(/\{\{req_key_control_q\}\}/g, t.req_key_control_q || 'Is the Control a Key Control to mitigate the risk of non-adherence for each mapped obligation/Regulation?')
    .replace(/\{\{product_label\}\}/g, t.product_label || 'Product')
    .replace(/\{\{product_q_label\}\}/g, t.product_q_label || 'Does the Control cover all applicable products within the line of business?')
    .replace(/\{\{segment_label\}\}/g, t.segment_label || 'Customer segment')
    .replace(/\{\{channel_label\}\}/g, t.channel_label || 'Channel')
    .replace(/\{\{performer_label\}\}/g, t.performer_label || 'Control Performer')
    .replace(/\{\{monitor_label\}\}/g, t.monitor_label || 'Monitor')
    .replace(/\{\{evidence_label\}\}/g, t.evidence_label || 'Control evidence')
    .replace(/\{\{instructions_label\}\}/g, t.instructions_label || 'Procedures / playbook')
    .replace(/\{\{sod_label\}\}/g, t.sod_label || 'Segregation of duties')
    .replace(/\{\{threshold_label\}\}/g, t.threshold_label || 'Breach / Concern / Acceptable bands');
}

interface QuestionTemplate {
  question?: string;
  guidance?: string;
  category?: string;
}

const QUESTION_TEMPLATES: Record<string, QuestionTemplate> = {
  C1: {
    category: '{{req_section_label}}',
    question: '{{req_key_control_q}}',
    guidance: 'If no, work with compliance to determine if {{req_field_label}}(s) should be unmapped from the Risk/Control. If determined there are no {{req_field_label}}s required then select N/A and provide rationale in comments.',
  },
  C2: {
    category: '{{req_section_label}}',
    question: 'Does the Control cover both ongoing compliance with the {{req_section_label}} and when changes are made to the current environment?',
    guidance: "If not, is there a related Control that covers what this Control doesn't care for? Cite Control ID in comments. If determined there are no {{req_field_label}}s required then select Yes and provide rationale in comments.",
  },
  C3: {
    category: 'Control Scope (Process, {{product_label}}s, {{segment_label}}s, {{channel_label}}s)',
    question: '{{product_q_label}}',
    guidance: 'If not, does the Control describe what {{product_label}}s are covered? Is there another Control that covers other applicable {{product_label}}s? Cite Control ID in comments',
  },
  C4: {
    category: 'Control Scope (Process, {{product_label}}s, {{segment_label}}s, {{channel_label}}s)',
    question: 'Does the Control cover all applicable {{segment_label}}s?',
    guidance: 'If not, does the Control describe what {{segment_label}}s are covered? Is there another related Control that covers all other {{segment_label}}s? Cite Control ID in comments',
  },
  C5: {
    category: 'Control Scope (Process, {{product_label}}s, {{segment_label}}s, {{channel_label}}s)',
    question: 'Does the Control cover all applicable {{channel_label}}s?',
    guidance: 'If not, does the Control describe what {{channel_label}} is covered? Is there another related Control that covers all other {{channel_label}}s? Cite Control ID in comments',
  },
  C7: {
    category: 'Risk Mitigation',
    question: 'Are all applicable {{product_label}}s mapped in organization Risk & Controls?',
  },
  C10: {
    category: '{{performer_label}}',
    question: 'Is the appropriate {{performer_label}} at the appropriate Level performing the Control?',
  },
  C11: {
    category: 'Control Execution',
    question: 'Are there instructions for performing the Control?',
    guidance: 'If yes, are they current and consistent with the Control Description? Link or Location of {{instructions_label}} for performing the Control',
  },
  C12: {
    category: 'Control Execution',
    question: 'Is there {{evidence_label}} of proper Control execution (in line with instructions) and retained?',
    guidance: 'In comments provide link or location of {{evidence_label}}',
  },
  M1: {
    category: 'Monitoring Execution',
    question: 'Who: Is the Monitoring performed by the Right {{monitor_label}} / Right Level?',
    guidance: 'Is the Monitoring performed by a knowledgeable person who is familiar with the Control. Is the reporting line clear from perceived conflict of interest? Is there clear {{sod_label}}? Is the person executing the Control independent from person executing Monitoring? Is there documentation in the monitoring procedures that clearly shows the {{sod_label}}?',
  },
  M5: {
    category: 'Monitoring Execution',
    question: 'Is there {{evidence_label}} of proper execution?',
    guidance: 'Consider if the Monitoring was properly executed. {{evidence_label}} of proper escalation when performance does not meet {{threshold_label}}?',
  },
  M6: {
    category: 'Monitoring Execution',
    question: 'Is there {{evidence_label}} of proper escalation when performance does not meet {{threshold_label}}?',
    guidance: 'Assess if person performing the Monitoring can articulate proper escalation process should he/she identify the Control is performing outside of acceptable {{threshold_label}}. Consider if Monitoring procedures clearly outlines proper escalation process when Control is performing outside of acceptable {{threshold_label}}',
  },
  M9: {
    category: 'Monitoring Documentation',
    question: 'Link or Location of {{instructions_label}} for performing the Monitoring',
    guidance: 'If link or location is provided, Version Control / Change Management history should be included in {{instructions_label}}',
  },
  CMA_C_WHO: {
    question: 'Is the control performed by the appropriate {{performer_label}} at the appropriate level?',
    guidance: 'Sufficient Authority? Possess proper competencies? Independent from the individual processing transactions? Appropriately resourced for Continuity?',
  },
  CMA_C_WHERE: {
    question: 'Is the control evidenced?',
    guidance: 'How and where is execution of the control documented and saved as {{evidence_label}}? Is the documentation sufficient and appropriate? Do both maker and checker document their performance of the control? Has record retention been considered?',
  },
  CMA_M_WHO: {
    question: 'Is the Monitoring performed by the appropriate {{monitor_label}} at the appropriate level?',
  },
  CMA_M_WHERE: {
    question: 'Is the Monitoring evidenced?',
    guidance: 'How and where is the Monitoring documented and saved as {{evidence_label}}? Record retention requirements should be outlined in the monitoring procedure.',
  },
};

export function adaptQuestion(
  qid: string,
  base: { question: string; guidance?: string; category: string },
  t: TerminologyProfile,
): { question: string; guidance?: string; category: string } {
  const template = QUESTION_TEMPLATES[qid];
  if (!template) return base;

  return {
    question: resolveTokens(template.question ?? base.question, t),
    guidance:
      template.guidance !== undefined
        ? resolveTokens(template.guidance, t)
        : base.guidance,
    category:
      template.category !== undefined
        ? resolveTokens(template.category, t)
        : base.category,
  };
}
